import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { relativeTime } from "../lib/format";
import { EmptyState } from "../components/EmptyState";
import { Modal } from "../components/Modal";
import { SkeletonPage } from "../components/Skeleton";
import { IconSearch, IconSpace } from "../components/Icons";

type Template = { id: string; name_en: string; name_de: string; deal_type: string; primary_locale?: string };
type Room = {
  id: string;
  name: string;
  room_type: string;
  status: string;
  document_count?: number;
  recipient_count?: number;
  updated_at?: string;
  last_visit_at?: string | null;
};

export function RoomsPage() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"blank" | "template">("blank");
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"updated" | "name" | "last_visit">("updated");

  const load = () => {
    setLoadErr(null);
    return Promise.all([
      api<{ rooms: Room[] }>("/api/v1/rooms"),
      api<{ templates: Template[] }>("/api/v1/rooms/templates"),
    ])
      .then(([r, t]) => {
        setRooms(r.rooms);
        setTemplates(t.templates);
      })
      .catch((e) => setLoadErr(String((e as Error).message || e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const filteredRooms = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...rooms];
    if (q) list = list.filter((r) => r.name.toLowerCase().includes(q));
    list.sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "last_visit") {
        return new Date(b.last_visit_at || 0).getTime() - new Date(a.last_visit_at || 0).getTime();
      }
      return new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime();
    });
    return list;
  }, [rooms, query, sort]);

  const create = async () => {
    if (!name.trim() || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setCreateError(null);
    try {
      const body =
        mode === "blank"
          ? { name: name.trim(), roomType: "client_portal", primaryLocale: "en" }
          : {
              name: name.trim(),
              templateId,
              primaryLocale: templates.find((t) => t.id === templateId)?.primary_locale || "de",
            };
      const res = await api<{ room: { id: string } }>("/api/v1/rooms", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setOpen(false);
      setName("");
      navigate(`/rooms/${res.room.id}?setup=1`);
    } catch (e) {
      setCreateError(String((e as Error).message || e || "Could not create Space."));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8, gap: 12 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 4 }}>
            Spaces
          </h1>
          <p className="page-lead" style={{ marginBottom: 0 }}>
            Secure rooms for deals, board packs, and diligence.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setMode("blank");
            setCreateError(null);
            setOpen(true);
          }}
        >
          Create Space
        </button>
      </div>

      {loading ? (
        <SkeletonPage title={false} />
      ) : loadErr ? (
        <div className="panel stack">
          <p role="alert" className="muted" style={{ margin: 0 }}>
            Could not load Spaces: {loadErr}
          </p>
          <button type="button" className="secondary" onClick={() => load()}>
            Retry
          </button>
        </div>
      ) : !rooms.length ? (
        <EmptyState
          headline="Create your first Space"
          body="Upload documents, share a secure link, and see who opens what, with deal-room folders when you need them."
          primaryLabel="Start blank"
          onPrimary={() => {
            setMode("blank");
            setOpen(true);
          }}
          secondaryLabel="Use a template"
          onSecondary={() => {
            setMode("template");
            setTemplateId(templates[0]?.id ?? "");
            setOpen(true);
          }}
        />
      ) : (
        <>
          <div className="row toolbar" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <label className="search-field" style={{ margin: 0, flex: 1, minWidth: 200 }}>
              <IconSearch className="search-field__icon" />
              <input
                className="input"
                placeholder="Search spaces"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search spaces"
              />
            </label>
            <select
              className="input"
              style={{ width: "auto", minWidth: 160 }}
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
              aria-label="Sort spaces"
            >
              <option value="updated">Recently updated</option>
              <option value="last_visit">Last visit</option>
              <option value="name">Name</option>
            </select>
          </div>
          {filteredRooms.length === 0 ? (
            <p className="muted">No spaces match &ldquo;{query.trim()}&rdquo;</p>
          ) : (
            <ul className="file-list rooms-list" role="list">
              {filteredRooms.map((r) => (
                <li key={r.id}>
                  <Link className="file-row" to={`/rooms/${r.id}`}>
                    <span className="file-row__glyph">
                      <IconSpace />
                    </span>
                    <span className="file-row__name">{r.name}</span>
                    <span className="file-row__meta">
                      <span className={`status-dot${r.status === "active" || r.status === "live" ? " is-live" : ""}`} />
                      {r.document_count ?? 0} {(r.document_count ?? 0) === 1 ? "doc" : "docs"} ·{" "}
                      {r.recipient_count ?? 0} {(r.recipient_count ?? 0) === 1 ? "person" : "people"}
                      {r.last_visit_at ? ` · visited ${relativeTime(r.last_visit_at)}` : ""}
                      {r.updated_at ? ` · updated ${relativeTime(r.updated_at)}` : ""}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {open && (
        <Modal
          title="Create Space"
          onClose={() => {
            setOpen(false);
            setCreateError(null);
          }}
          primaryLabel={busy ? "Creating…" : "Create"}
          primaryDisabled={!name.trim() || busy || (mode === "template" && !templateId)}
          onPrimary={() => void create()}
        >
          <div className="create-mode-toggle row" role="group" aria-label="Space type" style={{ marginBottom: 12 }}>
            <button type="button" className={mode === "blank" ? "" : "secondary"} onClick={() => setMode("blank")} aria-pressed={mode === "blank"}>
              Blank
            </button>
            <button
              type="button"
              className={mode === "template" ? "" : "secondary"}
              onClick={() => {
                setMode("template");
                if (!templateId && templates[0]) setTemplateId(templates[0].id);
              }}
              aria-pressed={mode === "template"}
            >
              From template
            </button>
          </div>
          <div className="stack">
            <label>
              Name
              <input
                className="input"
                value={name}
                autoFocus
                placeholder={mode === "blank" ? "Acme Q3 board pack" : "Project Helvetia"}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void create();
                }}
              />
            </label>
            {createError ? (
              <p className="muted" role="alert" style={{ margin: 0, color: "#9f1d2e" }}>
                {createError}
              </p>
            ) : null}
            {mode === "blank" ? (
              <p className="muted" style={{ margin: 0 }}>
                Empty space. Add folders and files yourself. Downloads on, NDA off by default.
              </p>
            ) : (
              <label>
                Template
                <select className="input" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name_en} · {t.deal_type}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
